<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto">
        <form @submit.prevent="saveContact">
            <div class="px-8 pt-6 pb-2">
                <div class="w-full xl:w-3/4 mx-auto flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <h1 class="stage-page-title">{{ pageTitle }}</h1>
                    <div class="inline-flex">
                        <router-link :to="relationshipsListPath" class="btn-stage-secondary mr-3">
                            {{ $t('Back') }}
                        </router-link>
                        <button :disabled="saving || loading" class="btn-stage-primary" type="submit">
                            {{ submitLabel }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <div class="w-full xl:w-3/4 mx-auto space-y-6">
                    <loading :status="loading || saving"/>

                    <div class="space-y-6">
                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Person und Zuordnung
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="external-contact-salutation">Anrede</label>
                                    <select
                                        id="external-contact-salutation"
                                        v-model="contact.salutation"
                                        class="stage-filter-input form-select block w-full text-sm sm:leading-5"
                                    >
                                        <option :value="null">Keine Auswahl</option>
                                        <option value="mr">Herr</option>
                                        <option value="ms">Frau</option>
                                        <option value="diverse">Divers</option>
                                    </select>
                                </div>
                                <div class="md:col-span-4">
                                    <label class="stage-filter-label block mb-2" for="external-contact-name">Name</label>
                                    <input
                                        id="external-contact-name"
                                        v-model.trim="contact.name"
                                        class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                        maxlength="255"
                                        required
                                    >
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="external-contact-role">Rolle / Funktion</label>
                                    <input
                                        id="external-contact-role"
                                        v-model.trim="contact.role_title"
                                        class="stage-filter-input form-input block w-full text-sm sm:leading-5"
                                        maxlength="255"
                                        placeholder="z. B. Ansprechpartnerin Marketing"
                                        required
                                    >
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="external-contact-company">Unternehmen</label>
                                    <input-select
                                        id="external-contact-company"
                                        v-model="contact.company_id"
                                        :options="options.companies"
                                        class="stage-filter-select"
                                        option-label="name"
                                        required
                                    >
                                        <template v-slot:selectedOption="props">
                                            <template v-if="!props || !props.anySelected">
                                                <span class="block truncate">Unternehmen auswählen</span>
                                            </template>
                                            <template v-else>
                                                <div class="flex min-w-0 items-center space-x-3">
                                                    <img
                                                        :alt="props.option && props.option.name ? props.option.name : 'Unternehmen'"
                                                        :src="resolveCompanyAvatar(props.option)"
                                                        class="h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                                    >
                                                    <span class="block truncate">{{ props.option ? props.option.name : 'Unternehmen auswählen' }}</span>
                                                </div>
                                            </template>
                                        </template>
                                        <template v-slot:selectOption="props">
                                            <div class="flex min-w-0 items-center space-x-3">
                                                <img
                                                    :alt="props.option && props.option.name ? props.option.name : 'Unternehmen'"
                                                    :src="resolveCompanyAvatar(props.option)"
                                                    class="h-6 w-6 flex-shrink-0 rounded-sm object-cover"
                                                >
                                                <span class="block truncate">{{ props.option ? props.option.name : '' }}</span>
                                            </div>
                                        </template>
                                        <template v-slot:notFound>
                                            <div class="font-normal">Keine Unternehmen verfügbar</div>
                                        </template>
                                    </input-select>
                                </div>
                                <div class="md:col-span-6">
                                    <label class="stage-filter-label block mb-2">Profilbild</label>
                                    <div class="flex items-center">
                                        <span
                                            v-if="!contact.avatar_preview"
                                            class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gray-200"
                                            aria-hidden="true"
                                        >
                                            <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.regular.user"></svg-vue>
                                        </span>
                                        <img
                                            v-else
                                            :src="contact.avatar_preview"
                                            alt="Vorschau des Profilbildes"
                                            class="h-12 w-12 flex-shrink-0 rounded-full object-cover"
                                        >
                                        <input
                                            ref="avatarInput"
                                            accept=".png,.jpg,.jpeg"
                                            hidden
                                            type="file"
                                            @change="changeAvatar"
                                        >
                                        <button
                                            class="ml-5 rounded border border-bordergray-100 bg-white px-3 py-2 text-sm font-medium leading-4 text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none"
                                            type="button"
                                            @click="$refs.avatarInput.click()"
                                        >
                                            {{ contact.avatar_preview ? 'Ändern' : 'Auswählen' }}
                                        </button>
                                        <button
                                            v-if="contact.avatar_preview"
                                            class="ml-3 text-sm text-gray-500 hover:text-gray-700 focus:outline-none"
                                            type="button"
                                            @click="removeAvatar"
                                        >
                                            Entfernen
                                        </button>
                                    </div>
                                    <p class="mt-2 text-xs leading-4 text-gray-500">
                                        Quadratisches PNG oder JPEG, maximal 1000 × 1000 Pixel.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Anschrift
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="external-contact-street">Straße</label>
                                    <input id="external-contact-street" v-model.trim="contact.street" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255">
                                </div>
                                <div class="md:col-span-1">
                                    <label class="stage-filter-label block mb-2" for="external-contact-house-number">Hausnummer</label>
                                    <input id="external-contact-house-number" v-model.trim="contact.house_number" placeholder="Nr" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="32">
                                </div>
                                <div class="md:col-span-2">
                                    <label class="stage-filter-label block mb-2" for="external-contact-postal-code">PLZ</label>
                                    <input id="external-contact-postal-code" v-model.trim="contact.postal_code" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="20" pattern="[A-Za-z0-9][A-Za-z0-9 \-]{1,19}" title="Bitte geben Sie eine gültige Postleitzahl ein.">
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="external-contact-city">Ort</label>
                                    <input id="external-contact-city" v-model.trim="contact.city" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="128">
                                </div>
                                <div class="md:col-span-3">
                                    <label class="stage-filter-label block mb-2" for="external-contact-country">Land</label>
                                    <input id="external-contact-country" v-model.trim="contact.country" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="128">
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Erreichbarkeit
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-email">E-Mail</label>
                                    <input id="external-contact-email" v-model.trim="contact.email" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" type="email">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-phone">Telefon</label>
                                    <input id="external-contact-phone" v-model.trim="contact.phone_landline" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="64" pattern="\+?[0-9][0-9 ()/.\-]{2,63}" title="Bitte geben Sie eine gültige Telefonnummer ein." type="tel">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-mobile">Mobil</label>
                                    <input id="external-contact-mobile" v-model.trim="contact.phone_mobile" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="64" pattern="\+?[0-9][0-9 ()/.\-]{2,63}" title="Bitte geben Sie eine gültige Telefonnummer ein." type="tel">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-website">Website</label>
                                    <input id="external-contact-website" v-model.trim="contact.website" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.beispiel.de" type="url">
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Social Media
                            </h3>
                            <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-linkedin">LinkedIn</label>
                                    <input id="external-contact-linkedin" v-model.trim="contact.linkedin_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.linkedin.com/in/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-xing">XING</label>
                                    <input id="external-contact-xing" v-model.trim="contact.xing_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.xing.com/profile/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-instagram">Instagram</label>
                                    <input id="external-contact-instagram" v-model.trim="contact.instagram_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.instagram.com/..." type="url">
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2" for="external-contact-facebook">Facebook</label>
                                    <input id="external-contact-facebook" v-model.trim="contact.facebook_url" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" placeholder="https://www.facebook.com/..." type="url">
                                </div>
                            </div>
                        </section>

                        <section class="overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                            <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">
                                Sichtbarkeit im Bereich Beziehungen
                            </h3>
                            <h4 class="mt-5 text-sm font-semibold text-gray-900">Interne Accounts</h4>
                            <div class="form-selection-panel mt-3 overflow-hidden">
                                <label class="form-selection-option flex cursor-pointer items-center px-3 py-2">
                                    <input v-model="contact.relationships_contact_visible_to_employees" class="form-checkbox mr-3 h-4 w-4 text-gray-700" type="checkbox">
                                    <span class="text-sm text-gray-700">Mitarbeiter</span>
                                </label>
                                <label class="form-selection-option flex cursor-pointer items-center px-3 py-2">
                                    <input v-model="contact.relationships_contact_visible_to_service_providers" class="form-checkbox mr-3 h-4 w-4 text-gray-700" type="checkbox">
                                    <span class="text-sm text-gray-700">Mitarbeitende Dienstleister</span>
                                </label>
                            </div>
                            <p class="mt-2 text-xs leading-5 text-gray-500">
                                Administratoren sehen diesen Kontakt unabhängig von dieser Auswahl.
                            </p>

                            <template v-if="isAdmin">
                                <h4 class="mt-5 text-sm font-semibold text-gray-900">Unternehmensgruppe (Kunde)</h4>
                                <p class="mt-1 text-xs leading-5 text-gray-500">
                                    Anwender mit ausgewählter Unternehmenszugehörigkeit können diesen Kontakt in Beziehungen sehen.
                                </p>
                                <company-selection
                                    v-model="contact.customer_company_ids"
                                    :companies="options.customer_companies"
                                    empty-text="Keine Kundenunternehmen verfügbar."
                                />

                                <h4 class="mt-5 text-sm font-semibold text-gray-900">Unternehmensgruppe (externer Kontakt)</h4>
                                <p class="mt-1 text-xs leading-5 text-gray-500">
                                    Anwender, die einem ausgewählten Unternehmen zugeordnet sind, haben Zugriff auf den Kontakt.
                                </p>
                                <company-selection
                                    v-model="contact.contact_company_ids"
                                    :companies="options.contact_companies"
                                    empty-text="Keine Kontaktunternehmen verfügbar."
                                />
                            </template>
                        </section>

                    </div>

                </div>
            </div>
        </form>
    </main>
</template>

<script>
import CompanySelection from '@/components/relationships/company-selection';
import {resolveStoreAccess} from '@/utils/access-context';
import {resolveReturnToPath} from '@/utils/list-route-state';

export default {
    name: 'workspace-relationships-external-contact-form',
    components: {
        CompanySelection,
    },
    metaInfo() {
        return {
            title: this.pageTitle,
        };
    },
    data() {
        return {
            loading: true,
            saving: false,
            options: {
                companies: [],
                customer_companies: [],
                contact_companies: [],
            },
            contact: {
                salutation: null,
                name: '',
                role_title: '',
                company_id: null,
                avatar: null,
                avatar_preview: null,
                avatar_removed: false,
                email: '',
                phone_landline: '',
                phone_mobile: '',
                website: '',
                linkedin_url: '',
                xing_url: '',
                instagram_url: '',
                facebook_url: '',
                street: '',
                house_number: '',
                postal_code: '',
                city: '',
                country: '',
                relationships_contact_visible_to_customers: false,
                relationships_contact_visible_to_employees: false,
                relationships_contact_visible_to_service_providers: false,
                customer_company_ids: [],
                contact_company_ids: [],
            },
        };
    },
    computed: {
        relationshipsListPath() {
            return resolveReturnToPath(this.$route.query || {}, '/workspace/relationships');
        },
        contactId() {
            const id = Number(this.$route.params.id || 0);
            return Number.isInteger(id) && id > 0 ? id : null;
        },
        isEditing() {
            return this.contactId !== null;
        },
        pageTitle() {
            return this.isEditing ? 'Externen Kontakt bearbeiten' : 'Person hinzufügen (externer Kontakt)';
        },
        submitLabel() {
            return 'Speichern';
        },
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        isAdmin() {
            return !!this.accessContext.is_admin;
        },
    },
    mounted() {
        this.loadPage();
    },
    beforeUnmount() {
        this.releaseAvatarPreview();
    },
    methods: {
        loadPage() {
            this.loading = true;
            const requests = [
                axios.get('api/workspace/relationships/options'),
            ];
            if (this.isEditing) {
                requests.push(axios.get(`api/workspace/relationships/external-contacts/${this.contactId}`));
            }

            Promise.all(requests).then(([optionsResponse, contactResponse]) => {
                this.options = {
                    companies: Array.isArray(optionsResponse.data?.companies) ? optionsResponse.data.companies : [],
                    customer_companies: Array.isArray(optionsResponse.data?.customer_companies) ? optionsResponse.data.customer_companies : [],
                    contact_companies: Array.isArray(optionsResponse.data?.contact_companies) ? optionsResponse.data.contact_companies : [],
                };
                if (this.isEditing) {
                    this.hydrateContact(contactResponse?.data?.contact);
                }
            }).catch(() => {
                this.$router.replace('/workspace/relationships');
            }).finally(() => {
                this.loading = false;
            });
        },
        hydrateContact(source) {
            if (!source || Number(source.entity_id || 0) !== this.contactId) {
                this.$router.replace('/workspace/relationships');
                return;
            }

            this.contact = {
                salutation: source.salutation || null,
                name: source.name || '',
                role_title: source.role_title || '',
                company_id: source.company_id || null,
                avatar: null,
                avatar_preview: source.avatar || null,
                avatar_removed: false,
                email: source.email || '',
                phone_landline: source.phone_landline || '',
                phone_mobile: source.phone_mobile || '',
                website: source.website || '',
                linkedin_url: source.linkedin_url || '',
                xing_url: source.xing_url || '',
                instagram_url: source.instagram_url || '',
                facebook_url: source.facebook_url || '',
                street: source.street || '',
                house_number: source.house_number || '',
                postal_code: source.postal_code || '',
                city: source.city || '',
                country: source.country || '',
                relationships_contact_visible_to_customers: !!source.relationships_contact_visible_to_customers,
                relationships_contact_visible_to_employees: !!source.relationships_contact_visible_to_employees,
                relationships_contact_visible_to_service_providers: !!source.relationships_contact_visible_to_service_providers,
                customer_company_ids: Array.isArray(source.customer_company_ids)
                    ? source.customer_company_ids.map((id) => Number(id))
                    : [],
                contact_company_ids: Array.isArray(source.contact_company_ids)
                    ? source.contact_company_ids.map((id) => Number(id))
                    : [],
            };
        },
        resolveCompanyAvatar(company) {
            return company && company.image ? company.image : '/images/default/icon.png';
        },
        changeAvatar(event) {
            const file = event.target.files && event.target.files.length ? event.target.files[0] : null;
            if (!file) {
                return;
            }

            if (!['image/png', 'image/x-png', 'image/jpeg'].includes(file.type)) {
                this.$notify({
                    title: this.$i18n.t('Error').toString(),
                    text: this.$i18n.t('Only image type files are accepted').toString(),
                    type: 'error',
                });
                event.target.value = '';
                return;
            }

            this.releaseAvatarPreview();
            this.contact.avatar = file;
            this.contact.avatar_preview = URL.createObjectURL(file);
            this.contact.avatar_removed = false;
        },
        removeAvatar() {
            this.releaseAvatarPreview();
            this.contact.avatar = null;
            this.contact.avatar_preview = null;
            this.contact.avatar_removed = this.isEditing;
            if (this.$refs.avatarInput) {
                this.$refs.avatarInput.value = '';
            }
        },
        releaseAvatarPreview() {
            if (this.contact.avatar_preview && String(this.contact.avatar_preview).startsWith('blob:')) {
                URL.revokeObjectURL(this.contact.avatar_preview);
            }
        },
        saveContact() {
            if (this.saving || this.loading) {
                return;
            }

            const formData = new FormData();
            [
                'salutation',
                'name',
                'role_title',
                'email',
                'phone_landline',
                'phone_mobile',
                'website',
                'linkedin_url',
                'xing_url',
                'instagram_url',
                'facebook_url',
                'street',
                'house_number',
                'postal_code',
                'city',
                'country',
            ].forEach((field) => formData.append(field, this.contact[field] ?? ''));

            formData.append('company_id', String(Number(this.contact.company_id)));
            formData.append('relationships_contact_visible_to_customers', this.contact.customer_company_ids.length > 0 ? '1' : '0');
            formData.append('relationships_contact_visible_to_employees', this.contact.relationships_contact_visible_to_employees ? '1' : '0');
            formData.append('relationships_contact_visible_to_service_providers', this.contact.relationships_contact_visible_to_service_providers ? '1' : '0');
            this.contact.customer_company_ids.forEach((id, index) => formData.append(`customer_company_ids[${index}]`, String(Number(id))));
            this.contact.contact_company_ids.forEach((id, index) => formData.append(`contact_company_ids[${index}]`, String(Number(id))));
            if (this.contact.avatar instanceof File) {
                formData.append('avatar', this.contact.avatar);
            }
            if (this.isEditing) {
                formData.append('_method', 'PUT');
                formData.append('avatar_remove', this.contact.avatar_removed ? '1' : '0');
            }

            this.saving = true;
            const endpoint = this.isEditing
                ? `api/workspace/relationships/external-contacts/${this.contactId}`
                : 'api/workspace/relationships/external-contacts';
            axios.post(endpoint, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(() => {
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t('Data saved correctly').toString(),
                    type: 'success',
                });
                this.$router.push(this.relationshipsListPath);
            }).finally(() => {
                this.saving = false;
            });
        },
    },
};
</script>
